/**
 * PPT-to-Quiz — upload .pptx → Claude generates MCQ + short-answer quiz.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  FileText, Upload, Sparkles, ArrowLeft, AlertCircle, Loader2, Check, X, Copy, Printer,
} from "lucide-react";
import PremiumNav from "@/components/premium/PremiumNav";
import PremiumFooter from "@/components/premium/PremiumFooter";
import { PremiumCard, PremiumButton, SectionHeading, Pill } from "@/components/premium/Primitives";

const API = process.env.REACT_APP_BACKEND_URL || '';
const MAX_BYTES = 15 * 1024 * 1024;

export default function PPTToQuiz() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [grade, setGrade] = useState("8");
  const [mcqCount, setMcqCount] = useState(8);
  const [shortCount, setShortCount] = useState(3);
  const [difficulty, setDifficulty] = useState("medium");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(pptx?)$/i.test(f.name)) {
      setError("Only .pptx or .ppt files are accepted.");
      return;
    }
    if (f.size > MAX_BYTES) {
      setError("File too large. Max 15 MB.");
      return;
    }
    setError(""); setFile(f);
  };

  const run = async () => {
    if (!file) { setError("Choose a .pptx file to upload."); return; }
    setLoading(true); setError(""); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("grade", grade);
      fd.append("mcq_count", String(mcqCount));
      fd.append("short_count", String(shortCount));
      fd.append("difficulty", difficulty);
      const r = await axios.post(`${API}/api/teaching/ppt-to-quiz`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setResult(r.data?.result || r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Upload/parse failed");
    } finally { setLoading(false); }
  };

  const copyJson = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(result, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_e) { /* clipboard unavailable */ }
  };

  return (
    <div className="min-h-screen bg-white font-sans" data-testid="teaching-tool-ppt-to-quiz">
      <PremiumNav />
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
        <button
          onClick={() => navigate("/teaching")}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#A855F7] mb-6"
          data-testid="teaching-back"
        >
          <ArrowLeft className="w-4 h-4"/> Back to Teaching Suite
        </button>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <SectionHeading
            eyebrow={<><FileText className="w-3 h-3 inline -mt-0.5 mr-1"/> Teaching Suite</>}
            title="PPT to Quiz"
            subtitle="Upload your .pptx slide deck — Claude reads every slide and generates a curriculum-aligned quiz with answer key."
          />
        </motion.div>

        {/* Upload + Options */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} className="mt-8">
          <PremiumCard className="p-6" hover={false}>
            <label className="block">
              <div className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-colors ${file ? "border-[#A855F7] bg-brand-50/40" : "border-slate-300 hover:border-[#A855F7] bg-slate-50/30"}`}>
                <input type="file" accept=".pptx,.ppt" onChange={onPick} className="hidden" data-testid="ppt-file-input"/>
                {file ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileText className="w-6 h-6 text-[#A855F7]"/>
                    <span className="font-semibold text-[#111111] text-sm">{file.name}</span>
                    <span className="text-xs text-slate-500">({(file.size / 1024).toFixed(0)} KB)</span>
                    <button
                      onClick={(e) => { e.preventDefault(); setFile(null); }}
                      className="ml-2 p-1 hover:bg-red-50 text-slate-400 hover:text-red-500 rounded"
                      data-testid="ppt-file-clear"
                    ><X className="w-4 h-4"/></button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                      <Upload className="w-5 h-5 text-slate-500"/>
                    </div>
                    <div className="text-sm font-semibold text-[#111111]">Drop .pptx file here or click to browse</div>
                    <div className="text-xs text-slate-500">Max 15 MB · .pptx and .ppt supported</div>
                  </div>
                )}
              </div>
            </label>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Grade</label>
                <select value={grade} onChange={(e) => setGrade(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" data-testid="ppt-grade">
                  {["K","1","2","3","4","5","6","7","8","9","10","11","12"].map((g) => <option key={g}>{g}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">MCQs</label>
                <input type="number" min={0} max={20} value={mcqCount} onChange={(e) => setMcqCount(Number(e.target.value))} className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" data-testid="ppt-mcq-count"/>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Short</label>
                <input type="number" min={0} max={10} value={shortCount} onChange={(e) => setShortCount(Number(e.target.value))} className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" data-testid="ppt-short-count"/>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Difficulty</label>
                <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" data-testid="ppt-difficulty">
                  {["easy","medium","hard","mixed"].map((d) => <option key={d}>{d}</option>)}
                </select>
              </div>
            </div>

            <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
              <p className="text-xs text-slate-500">Parsing + Claude generation typically takes 15-45 seconds.</p>
              <PremiumButton onClick={run} loading={loading} icon={Sparkles} size="lg" disabled={!file || loading} data-testid="ppt-run">
                {loading ? "Generating quiz…" : "Generate quiz"}
              </PremiumButton>
            </div>
            {error && (
              <div className="mt-4 text-sm text-red-600 flex items-center gap-2" data-testid="ppt-error">
                <AlertCircle className="w-4 h-4"/> {error}
              </div>
            )}
          </PremiumCard>
        </motion.div>

        {loading && (
          <div className="mt-10 flex flex-col items-center justify-center text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin text-[#A855F7]"/>
            <div className="mt-3 text-sm">Reading slides + generating quiz…</div>
          </div>
        )}

        {result && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-10 space-y-6" data-testid="ppt-result">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h2 className="font-heading text-xl font-semibold text-[#111111]">Result</h2>
              <div className="flex items-center gap-2">
                <PremiumButton variant="secondary" size="sm" icon={copied ? Check : Copy} onClick={copyJson}>{copied ? "Copied" : "Copy JSON"}</PremiumButton>
                <PremiumButton variant="secondary" size="sm" icon={Printer} onClick={() => window.print()}>Print / PDF</PremiumButton>
              </div>
            </div>

            <PremiumCard className="p-6" hover={false}>
              <h2 className="font-heading text-2xl font-bold text-[#111111]">{result.title}</h2>
              <div className="flex flex-wrap gap-2 mt-3">
                <Pill variant="brand">Grade {result.grade}</Pill>
                <Pill variant="outline">{result.slide_count} slides</Pill>
                <Pill variant="dark">{result.total_marks} marks</Pill>
              </div>
              <p className="mt-2 text-xs text-slate-500">Source: {result.source}</p>
              {(result.topics_covered || []).length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {result.topics_covered.map((t, i) => <Pill key={i} variant="neutral">{t}</Pill>)}
                </div>
              )}
            </PremiumCard>

            {(result.mcq || []).length > 0 && (
              <PremiumCard className="p-6" hover={false}>
                <h3 className="font-heading text-lg font-semibold text-[#111111] mb-4">Section A — Multiple choice</h3>
                <div className="space-y-3">
                  {result.mcq.map((q, i) => (
                    <div key={i} className="p-4 rounded-xl border border-slate-200">
                      <div className="flex items-start justify-between flex-wrap gap-2">
                        <p className="text-sm font-semibold text-[#111111] flex-1">Q{i + 1}. {q.q}</p>
                        <div className="flex items-center gap-2">
                          {q.slide_ref && <Pill variant="outline">Slide {q.slide_ref}</Pill>}
                          <Pill variant="brand">{q.marks} marks</Pill>
                        </div>
                      </div>
                      <ul className="mt-2 space-y-1 text-sm text-slate-700">
                        {q.options.map((o, j) => <li key={j}>{o}</li>)}
                      </ul>
                      <p className="mt-2 text-xs text-emerald-700"><b>Answer:</b> {q.answer}</p>
                      {q.explanation && <p className="mt-1 text-xs text-slate-500">{q.explanation}</p>}
                    </div>
                  ))}
                </div>
              </PremiumCard>
            )}

            {(result.short || []).length > 0 && (
              <PremiumCard className="p-6" hover={false}>
                <h3 className="font-heading text-lg font-semibold text-[#111111] mb-4">Section B — Short answers</h3>
                <div className="space-y-3">
                  {result.short.map((q, i) => (
                    <div key={i} className="p-4 rounded-xl border border-slate-200">
                      <div className="flex items-start justify-between flex-wrap gap-2">
                        <p className="text-sm font-semibold text-[#111111] flex-1">Q{i + 1}. {q.q}</p>
                        <div className="flex items-center gap-2">
                          {q.slide_ref && <Pill variant="outline">Slide {q.slide_ref}</Pill>}
                          <Pill variant="brand">{q.marks} marks</Pill>
                        </div>
                      </div>
                      <p className="mt-2 text-xs text-emerald-700"><b>Model answer:</b> {q.answer}</p>
                    </div>
                  ))}
                </div>
              </PremiumCard>
            )}
          </motion.div>
        )}
      </div>
      <PremiumFooter />
    </div>
  );
}
